import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import LeakCard from "@/components/feature/LeakCard";
import {
  SITE_ORIGIN,
  formatHot,
  getCategoryName,
  getLeakById,
  getRelatedLeaks,
} from "@/lib/leaks";

export default function LeakDetail() {
  const { id } = useParams<{ id: string }>();
  const leak = id ? getLeakById(id) : undefined;

  if (!leak) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这条爆料暂时找不到了
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            可能是链接有误，或该内容仍在整理中。你可以回到列表页继续浏览其他现场线索。
          </p>
          <Link
            to="/feed"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回爆料列表
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const categoryName = getCategoryName(leak.category);
  const related = getRelatedLeaks(leak, 3);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: leak.title,
      description: leak.summary,
      datePublished: leak.publishedAt,
      articleSection: categoryName,
      keywords: leak.tags.join(","),
      url: `${SITE_ORIGIN}/leak/${leak.id}`,
      author: { "@type": "Organization", name: "蝌蚪爆料编辑组" },
      publisher: { "@type": "Organization", name: "蝌蚪爆料" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "最新爆料", item: `${SITE_ORIGIN}/feed` },
        {
          "@type": "ListItem",
          position: 3,
          name: categoryName,
          item: `${SITE_ORIGIN}/topic/${leak.category}`,
        },
        { "@type": "ListItem", position: 4, name: leak.title },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${leak.title} | 蝌蚪爆料现场快讯`}
        description={`${leak.summary.slice(0, 90)}`}
        keywords={`${leak.tags.join(",")},蝌蚪爆料,蝌蚪爆料最新,蝌蚪爆料现场`}
        path={`/leak/${leak.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 md:px-8 md:pt-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "最新爆料", to: "/feed" },
            { label: categoryName, to: `/topic/${leak.category}` },
            { label: leak.title },
          ]}
        />
      </div>

      <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 py-8 md:px-8 md:py-10 lg:grid-cols-12 lg:gap-12">
        <article className="lg:col-span-8">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
            <i className="ri-bookmark-line" />
            {categoryName}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {leak.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line" />
              {leak.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {leak.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line" />
              {leak.readingTime}
            </span>
            <span className="flex items-center gap-1.5 text-primary-600">
              <i className="ri-fire-line" />
              {formatHot(leak.hot)} 热度
            </span>
          </div>

          <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src={leak.cover}
              alt={`${leak.title} — 蝌蚪爆料现场画面`}
              title={`${leak.title} 蝌蚪爆料`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
              <i className="ri-lightbulb-flash-line" />
              要点速览
            </h2>
            <ul className="mt-3 space-y-2">
              {leak.highlights.map((item) => (
                <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-accent-900">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 space-y-5">
            {leak.content.map((paragraph, index) => (
              <p key={index} className="text-[15px] leading-[1.9] text-foreground-800">
                {paragraph}
              </p>
            ))}
          </div>

          <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-time-line text-primary-500" />
              事件时间线
            </h2>
            <ol className="mt-5">
              {leak.timeline.map((item, index) => (
                <li key={index} className="relative flex gap-4 pb-5 last:pb-0">
                  <span className="relative flex flex-col items-center">
                    <span className="flex h-2.5 w-2.5 items-center justify-center rounded-full bg-primary-500 ring-4 ring-primary-100" />
                    {index !== leak.timeline.length - 1 ? (
                      <span className="mt-1 w-px flex-1 bg-background-300" />
                    ) : null}
                  </span>
                  <span className="flex flex-col pb-1">
                    <span className="font-heading text-sm font-bold text-foreground-950">
                      {item.time}
                    </span>
                    <span className="mt-1 text-sm leading-relaxed text-foreground-700">
                      {item.text}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-foreground-500">相关标签：</span>
            {leak.tags.map((tag) => (
              <Link
                key={tag}
                to={`/feed?q=${encodeURIComponent(tag)}`}
                className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <p className="mt-8 rounded-md border border-background-200 bg-background-50 px-4 py-3 text-xs leading-relaxed text-foreground-500">
            说明：本文内容基于公开线索与现场描述整理，涉及未证实的部分已在正文中标注。文中所涉事件与人物均为虚构创作，仅供阅读参考。
          </p>
        </article>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-links-line text-secondary-600" />
              相关爆料推荐
            </h2>
            <div className="mt-5 space-y-5">
              {related.map((item) => (
                <LeakCard key={item.id} leak={item} showSummary={false} />
              ))}
            </div>
            <Link
              to={`/topic/${leak.category}`}
              className="mt-6 flex items-center justify-center gap-1.5 rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              查看更多「{categoryName}」
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}